import { Avatar } from '@mui/material';
import { useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { formatFileSize, getShortName, useJunoCommonFunctions } from '../../../client/Juno/helpers';
import JunoButton from '../common/JunoButton';
import { apiService } from '../../../client/common/apiCallService';

const ProfileImage = ({ isMobile }) => {
    const clientData = useSelector(state => state.juno.clientData);
    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const apiUrl = useSelector(state => state.config.api_url);
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const { updateUsersData, getClientData } = useJunoCommonFunctions();

    const handleFile = files => {
        if (files && files[0]) {
            const file = files[0];
            const allowedTypes = ['image/jpeg', 'image/png'];
            const maxSize = 100 * 1024; // 100KB
            const maxDimensions = 300;

            if (!allowedTypes.includes(file.type)) {
                setError('Invalid file type. Please select a JPG or PNG image.');
                setFile(null);
                return;
            }

            if (file.size > maxSize) {
                setError('File size exceeds the 100KB limit.');
                setFile(null);
                return;
            }

            const previewUrl = URL.createObjectURL(file);
            const img = new Image();
            img.src = previewUrl;

            img.onload = () => {
                if (img.width > maxDimensions || img.height > maxDimensions) {
                    setError('Image dimensions must not exceed 300x300 pixels.');
                    URL.revokeObjectURL(previewUrl);
                    return;
                }

                setFile(
                    Object.assign(file, {
                        preview: previewUrl,
                    }),
                );
            };

            img.onerror = () => {
                setError('Could not read the selected file. Please try another image.');
                URL.revokeObjectURL(previewUrl);
            };

            setError(null);
        }
    };

    const handleDrag = function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files);
        }
    };

    const handleChange = e => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files);
        }
    };

    const handleImageUpload = () => {
        setLoading(true);
        setError(null);

        const payload = {
            imageType: 'userProfileImages',
            fileName: file.name,
        };

        apiService(
            (window.location.hostname === 'localhost' ? apiUrl : '') +
                '/restapi/get-client-presigned-url',
            {
                ...payload,
            },
            async resp => {
                try {
                    const uploadResult = await fetch(resp.data[0], {
                        method: 'PUT',
                        body: file,
                        headers: {
                            'Content-Type': 'application/*',
                            'cache-control': 'public, max-age=0',
                        },
                    });

                    if (!uploadResult.ok) {
                        setError('Image upload failed. Please try again.');
                    } else {
                        const path = `/images/profile-images/${clientData.clientId}.${file.name?.split('.')?.pop()}`;
                        updateUsersData({
                            profileImage: {
                                path,
                                url: `${imageBaseUrl}${path.replace('/images', '')}?v=${Date.now()}`,
                            },
                        }).then(() => getClientData());
                    }
                } catch (err) {
                    setError(err?.message || 'An error occurred during image upload.');
                } finally {
                    setLoading(false);
                }
            },
            err => {
                setError(err?.error || 'An error occurred while fetching the upload URL.');
                setLoading(false);
            },
        );
    };

    return (
        <div className="h-full max-h-screen flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto p-3 md:p-6 bg-modal-bg md:bg-transparent text-text">
                {isMobile ? null : (
                    <div className="pb-3 self-stretch inline-flex justify-between items-center gap-3 w-full">
                        <div className="flex-1 flex justify-between items-center gap-1 grow-1">
                            <h2 className="justify-start text-profile-primary text-sm font-semibold leading-tight uppercase">
                                personal image
                            </h2>
                            <JunoButton
                                dataE2e="add-new-btn"
                                className={
                                    '!w-auto !px-2 !py-1.5 !h-auto font-semibold text-xs justify-end'
                                }
                                disabled={!file || loading}
                                onClick={() => handleImageUpload()}>
                                {loading ? 'Uploading...' : 'SET IMAGE'}
                            </JunoButton>
                        </div>
                    </div>
                )}
                <div className="self-stretch flex flex-col justify-start items-start gap-6">
                    <div className="inline-flex justify-start items-center gap-8">
                        <Avatar
                            src={file?.preview || clientData?.profileImage?.url}
                            variant="square"
                            imgProps={{
                                style: {
                                    objectFit: 'fill',
                                },
                            }}
                            sx={{
                                width: 128,
                                height: 128,
                                bgcolor: 'var(--color-text)',
                                borderRadius: '8px',
                                outline: '1px solid',
                                outlineColor: 'var(--color-text)',
                            }}>
                            <div className="text-container-bg uppercase text-5xl font-semibold font-['Inter'] leading-[64px] !outline-0">
                                {getShortName(clientData?.name)}
                            </div>
                        </Avatar>
                    </div>
                    <div className="self-stretch h-px relative bg-section-br" />
                    <div className="flex flex-col justify-start items-start gap-6 w-full">
                        <div className="flex flex-col justify-center items-start gap-2">
                            <h2 className="text-[#334155] dark:text-[#d4d4d8] justify-start text-sm font-semibold leading-tight">
                                YOUR PHOTO
                            </h2>
                            <span className="justify-center text-profile-image-req text-xs font-medium uppercase leading-none">
                                This will be displayed on your profile.
                            </span>
                        </div>
                        <div onDragEnter={handleDrag} className="w-full lg:w-[460px]">
                            <input
                                ref={inputRef}
                                type="file"
                                id="input-file-upload"
                                multiple={false}
                                onChange={handleChange}
                                className="hidden"
                                accept="image/png, image/jpeg"
                            />
                            <label
                                className={`w-full px-4 py-6 bg-black/5 dark:bg-white/5 rounded-lg outline outline-2 outline-offset-[-1px] outline-input-border flex flex-col justify-center items-center gap-3 cursor-pointer ${
                                    dragActive ? 'drag-active' : ''
                                } outline-dashed`}
                                htmlFor="input-file-upload">
                                <svg
                                    width="32"
                                    height="32"
                                    viewBox="0 0 32 32"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M11.1984 20.8001L15.9987 15.9998M15.9987 15.9998L20.799 20.8001M15.9987 15.9998V26.8004M25.5992 21.6915C27.0651 20.4809 27.9993 18.6495 27.9993 16.5998C27.9993 12.9546 25.0443 9.99948 21.399 9.99948C21.1368 9.99948 20.8914 9.86267 20.7583 9.63674C19.1933 6.98112 16.304 5.19922 12.9985 5.19922C8.0277 5.19922 3.99805 9.22887 3.99805 14.1997C3.99805 16.6792 5.00064 18.9245 6.62253 20.5523"
                                        stroke="var(--color-text)"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                                <div className="self-stretch flex flex-col justify-start items-center gap-2">
                                    <div className="self-stretch inline-flex justify-center items-start gap-1">
                                        <button
                                            type="button"
                                            onClick={() => inputRef.current.click()}
                                            className="justify-center text-xs font-semibold uppercase leading-none">
                                            Click to upload
                                        </button>
                                        <span className="justify-center text-profile-image-req text-xs font-medium uppercase leading-none">
                                            or drag and drop
                                        </span>
                                    </div>
                                    <div className="self-stretch inline-flex justify-center items-start gap-1">
                                        <span className="justify-center text-profile-image-req text-xs font-medium uppercase leading-none">
                                            PNG OR JPEG (max. 300x300px)
                                        </span>
                                    </div>
                                </div>
                            </label>
                            {dragActive && (
                                <div
                                    id="drag-file-element"
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}></div>
                            )}
                        </div>
                        {file && (
                            <div className="w-full lg:w-[460px] px-4 py-3 bg-input rounded-lg outline outline-1 outline-offset-[-1px] outline-input-border flex flex-col justify-center items-start gap-3">
                                <div className="self-stretch inline-flex justify-start items-center gap-1">
                                    <div className="flex-1 flex justify-between items-center">
                                        <span className="flex-1 justify-center text-sm font-medium uppercase leading-tight truncate max-w-52 md:max-w-96">
                                            {file.name}
                                        </span>
                                        <span className="justify-center text-profile-image-req text-sm font-medium uppercase leading-tight">
                                            {formatFileSize(file.size)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {error && <span className="text-red-500 text-xs">{error}</span>}
                    </div>
                    <div className="self-stretch h-px relative bg-section-br" />
                    <div className="flex flex-col justify-center items-start gap-2">
                        <h2 className="justify-start text-sm font-semibold uppercase leading-tight">
                            Image Requirements
                        </h2>
                        <ul className="px-4 flex flex-col justify-center items-start gap-1 list-disc list-outside marker:text-profile-image-req">
                            <li className="justify-center">
                                <span className="text-profile-image-req text-xs font-medium uppercase leading-tight">
                                    The image must be in{' '}
                                    <span className="text-text text-xs font-semibold uppercase leading-tight">
                                        PNG
                                    </span>{' '}
                                    OR{' '}
                                    <span className="text-text text-xs font-semibold uppercase leading-tight">
                                        JPEG
                                    </span>{' '}
                                    format.
                                </span>
                            </li>
                            <li className="justify-center">
                                <span className="text-profile-image-req text-xs font-medium uppercase leading-tight">
                                    Maximum file size:{' '}
                                </span>
                                <span className="text-text text-xs font-semibold uppercase leading-tight">
                                    100 KB
                                </span>
                                .
                            </li>
                            <li className="justify-center">
                                <span className="text-profile-image-req text-xs font-medium uppercase leading-tight">
                                    Maximum dimensions:{' '}
                                </span>
                                <span className="text-text text-xs font-semibold uppercase leading-tight">
                                    300 x 300 pixels.
                                </span>
                            </li>
                            <li className="justify-center">
                                <span className="text-profile-image-req text-xs font-medium uppercase leading-tight">
                                    The image must{' '}
                                    <span className="text-text text-xs font-semibold uppercase leading-tight">
                                        not contain pornographic content
                                    </span>{' '}
                                    or{' '}
                                    <span className="text-text text-xs font-semibold uppercase leading-tight">
                                        offensive language.
                                    </span>
                                </span>
                            </li>
                            <li className="justify-center">
                                <span className="text-profile-image-req text-xs font-medium uppercase leading-tight">
                                    The image must{' '}
                                    <span className="text-text text-xs font-semibold uppercase leading-tight">
                                        not infringe any copyright
                                    </span>{' '}
                                    or{' '}
                                    <span className="text-text text-xs font-semibold uppercase leading-tight">
                                        intellectual property rights.
                                    </span>
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <JunoButton
                dataE2e="set-image"
                disabled={!file || loading}
                className={'block lg:hidden'}
                onClick={() => handleImageUpload()}>
                {loading ? 'Uploading...' : 'SET IMAGE'}
            </JunoButton>
        </div>
    );
};

export default ProfileImage;
